import WebAPIUtils from '../../WebAPIUtils';
import type { Dispatch } from 'redux';
import { setDates, setDate, overrideUserSelectedDate } from '../../reducers/FieldReducer';
import Field from '../../model/Field';
import Survey from '../Prescription/PrescriptionJob';
import { SURVEY_LAYERS } from '../../constants/SurveyLayers';

export const intersectDates = (arrays: any[][]) => {
  if (!arrays.length) return [];
  return arrays.reduce((acc, curr) => acc.filter((date) => curr.includes(date)));
};

export const handleSatelliteLayer = async ({
  selectedFields,
  date,
  datesCacheRef,
  dispatch,
}: {
  selectedFields: Field[];
  date: string;
  datesCacheRef: React.MutableRefObject<Map<number, string[]>>;
  dispatch: Dispatch;
}) => {
  const results = await Promise.all(
    selectedFields.map(async (field) => {
      if (!field.bundle) return { ...field, error: true, dates: null };

      if (
        datesCacheRef.current.has(field.fieldId) &&
        datesCacheRef.current.get(field.fieldId) !== null
      ) {
        return { ...field, error: false, dates: datesCacheRef.current.get(field.fieldId) };
      }

      const result = await WebAPIUtils.getBundleStats(field.bundle, 'ndvi');

      if (!result || result.length === 0) return { ...field, error: true, dates: null };

      const dates = result.map((stat) => stat.date);
      datesCacheRef.current.set(field.fieldId, dates);

      if (selectedFields.length > 1 && !includesSelectedDate(dates, date)) {
        return { ...field, error: true, dates: null };
      }

      return { ...field, error: false, dates };
    })
  );

  const validDates = results.filter((r) => !r.error && r.dates).map((r) => r.dates);
  const commonDates = intersectDates(validDates);
  dispatch(setDates(commonDates));

  if (commonDates.indexOf(date) === -1) {
    dispatch(overrideUserSelectedDate(commonDates[commonDates.length - 1]));
  }

  return results.map((r) => {
    const field = { ...r };
    delete field.dates;
    return field;
  });
};

const includesSelectedDate = (dates, selectedDate: string) => {
  const foundDate = Object.values(dates).find((date: string) => date === selectedDate);
  return !!foundDate;
};

export const handleSurveyLayer = (
  selectedFields: Field[],
  surveys: Survey[],
  selectedLayer: typeof SURVEY_LAYERS
) => {
  return selectedFields.map((field) => {
    const fieldSurveys = surveys?.filter((survey) => survey.fieldId === field.fieldId);
    const surveyHasLayer =
      fieldSurveys && fieldSurveys.some((survey) => survey?.newImages[selectedLayer]);

    return {
      ...field,
      error: !surveyHasLayer,
    };
  });
};

export const areAllFieldsOfCropSelected = (
  cropType: string,
  selectedFields: Field[],
  grouped: Record<string, Field[]>
) => {
  const fieldsForCrop = grouped[cropType] || [];
  return (
    fieldsForCrop.length > 0 &&
    fieldsForCrop.every((field) =>
      selectedFields.some((selected) => selected.fieldId === field.fieldId)
    )
  );
};

export const jobNameWithFieldName = (bulkPrescriptionPrefix: string, fieldName: string): string => {
  const dashedName = fieldName
    .trim() // Trim leading/trailing whitespace
    .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
    .replace(/"/g, '') // Remove quotation marks
    .replace(/[ .]/g, '-'); // Replace all spaces and dots with dashes
  return dashedName.includes(bulkPrescriptionPrefix)
    ? dashedName
    : `${bulkPrescriptionPrefix}-${dashedName}`;
};
